import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { authService } from "../services/auth";
import { postAuthDestination } from "../utils/reviewerNav";
import { Button } from "@/design-system/components/react";

const OAuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthState } = useAuth();
  const [error, setError] = useState<string>("");
  // Prevent React StrictMode from calling OAuth callback twice
  const hasRun = React.useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate calls in React StrictMode (development double-invoke)
      if (hasRun.current) {
        return;
      }
      hasRun.current = true;

      try {
        // Extract authorization code from URL
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        const errorParam = params.get("error");

        if (errorParam) {
          setError(`OAuth error: ${errorParam}`);
          return;
        }

        if (!code) {
          setError("No authorization code received from authentication provider");
          return;
        }

        // Call backend API to exchange code for tokens
        const authData = await authService.cognitoCallback(code);

        // Set auth context (include userType so Navbar and AgreementGate work immediately)
        setAuthState({
          isAuthenticated: true,
          token: authData.token,
          refreshToken: authData.refreshToken,
          userId: authData.userId,
          isAdmin: authData.isAdmin,
          adminUserId: authData.adminUserId,
          userType: authData.userType,
          agreementAccepted: authData.agreementAccepted,
        });

        // Restore intended destination saved before OAuth redirect. A genuine
        // deep link wins for every role (PrivateRoute still enforces per-role
        // access); otherwise fall back to the shared role-based landing so this
        // path stays in sync with the email/password path.
        const savedRedirect = sessionStorage.getItem("auth_redirect");
        sessionStorage.removeItem("auth_redirect");
        const destination =
          savedRedirect ||
          postAuthDestination({
            userType: authData.userType,
            allowedCohorts: authData.allowedCohorts,
          });

        // Route to agreement gate if user hasn't accepted yet
        if (!authData.agreementAccepted) {
          navigate("/agreement", {
            state: { from: { pathname: destination } },
          });
        } else {
          navigate(destination);
        }
      } catch (err: any) {
        setError(err?.response?.data?.detail || "Failed to complete OAuth login");
      }
    };

    handleCallback();
  }, [location, navigate, setAuthState]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-fg">
        <div className="flex w-full max-w-sm flex-col items-center text-center">
          <img src="/images/a2z-logo-dark.png" alt="a2z Radiology AI" className="mb-8 w-40" />
          <p className="mb-1 flex items-center gap-2 text-sm text-warn">
            <span aria-hidden>⚠</span>
            {error}
          </p>
          <p className="mb-6 text-xs text-fg-muted">Please sign in again to continue.</p>
          <Button onClick={() => navigate("/login")} className="w-full">
            Return to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg text-fg">
      <div className="flex flex-col items-center text-center">
        <img src="/images/a2z-logo-dark.png" alt="a2z Radiology AI" className="w-40" />
        <div className="mt-6 h-0.5 w-28 overflow-hidden">
          <div className="fetch-sweep h-full w-full" />
        </div>
        <p className="mt-4 text-sm text-fg-muted">Signing in…</p>
      </div>
    </div>
  );
};

export default OAuthCallback;
